/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-floating-promises, react-hooks/exhaustive-deps, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any, @typescript-eslint/strict-boolean-expressions */

import { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { SxProps, Theme, useTheme } from '@mui/material/styles';
import { Box } from '@mui/material';
import useIsMobile from 'hooks/useIsMobile';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  ReferenceLine,
  Brush,
} from 'recharts';
import {
  IndicesContentType,
  IndexSymbol,
  OIVolume,
  PutCall,
  RawModelRow,
  SymSelectorSettings,
  BrushZoomConfig,
} from '../../../types';
import {
  TIMESTAMP_TICK_CONFIG,
  formatAsCompactNumberCallback,
  formatAsCurrency,
  roundToNearest,
} from '../../../util';
import {
  getTicksBrushed,
  getZoomConfigRefArea,
  updateBrushZoomConfig,
} from 'util/shared/chart';
import {
  DEFAULT_BRUSH_ZOOM_CONFIG,
  DEFAULT_CHART_MARGINS,
  DEFAULT_X_AXIS_STYLES,
  DEFAULT_Y_AXIS_STYLES,
} from '../../../config';
import { IndicesHeader } from '../shared/IndicesHeader';
import { Controls } from '../../shared/Controls';
import SettingsPopout from '../../shared/SettingsPopout';
import { ZoomOutButton } from 'components/shared';
import useModels from '../../../hooks/indices/useModels';
import ChartWatermarkContainer from 'components/shared/ChartWatermarkContainer';
import useBrushZoom from 'hooks/useBrushZoom';

const OI_TYPE = {
  CHANGE: 'change',
  TOTAL: 'total',
};

export const OIVolumeControls = ({
  oiType,
  setOiType,
  optType,
  setOptType,
  oiVolumeData,
}: {
  oiType: string;
  setOiType: React.Dispatch<React.SetStateAction<string>>;
  optType: PutCall;
  setOptType: React.Dispatch<React.SetStateAction<PutCall>>;
  oiVolumeData: OIVolume[];
}) => {
  const isMobile = useIsMobile();
  const theme = useTheme();
  const [zoomConfig, setZoomConfig] = useState<BrushZoomConfig>(
    DEFAULT_BRUSH_ZOOM_CONFIG,
  );
  const controls = (
    <Controls
      buttonGroups={[
        {
          buttons: [
            { value: OI_TYPE.CHANGE, label: 'OI Change' },
            { value: OI_TYPE.TOTAL, label: 'Total OI & Volume' },
          ],
          setter: setOiType,
          curValue: oiType,
        },
        {
          buttons: [
            { value: PutCall.PUT, label: 'Put' },
            { value: PutCall.CALL, label: 'Call' },
          ],
          setter: setOptType,
          curValue: optType,
        },
      ]}
    />
  );
  let settings = controls;
  if (isMobile) {
    settings = (
      <SettingsPopout
        title="Settings"
        popperID="oiVolumeControls"
        sx={{ backgroundColor: theme.palette.core.translucentBG }}
      >
        {controls}
      </SettingsPopout>
    );
  }

  return (
    <>
      {settings}
      <ZoomOutButton
        zoomConfig={zoomConfig}
        setZoomConfig={setZoomConfig}
        initialData={oiVolumeData}
        overrideDefault={{
          leftIdx: DEFAULT_BRUSH_ZOOM_CONFIG.leftIdx,
          rightIdx: oiVolumeData.length - 1,
        }}
      />
    </>
  );
};

// Round to the nearest $X
const ROUNDINGS = new Map([
  [IndexSymbol.SPX, 5],
  [IndexSymbol.NDX, 10],
  [IndexSymbol.RUT, 5],
  [IndexSymbol.SPY, 0.5],
  [IndexSymbol.QQQ, 0.5],
  [IndexSymbol.IWM, 0.5],
]);

interface OIVolumeChartProps {
  chartStyleOverrides?: React.CSSProperties;
  selectedSym: string;
  containerStyleOverrides?: SxProps<Theme>;
  symSelectorSettings?: SymSelectorSettings;
}

export const OIVolumeChart = ({
  chartStyleOverrides,
  selectedSym,
  containerStyleOverrides,
  symSelectorSettings,
}: OIVolumeChartProps) => {
  const ref = useRef<HTMLInputElement | null>(null);
  const theme = useTheme();
  const { getModels } = useModels();
  const [optType, setOptType] = useState<PutCall>(PutCall.PUT);
  const [oiType, setOiType] = useState<string>(OI_TYPE.TOTAL);
  const [zoomConfig, setZoomConfig] = useState<BrushZoomConfig>(
    DEFAULT_BRUSH_ZOOM_CONFIG,
  );
  const [oiVolumeData, setOiVolumeData] = useState<OIVolume[]>([]);
  const keySuffix = oiType === OI_TYPE.CHANGE ? '_change' : '';

  const roundTo: number = useMemo(
    () => ROUNDINGS.get(selectedSym as IndexSymbol)!,
    [selectedSym],
  );
  const roundStrike = useCallback(
    (s: number) => roundToNearest(s, roundTo),
    [roundTo],
  );

  const { zoomChartConfig } = useBrushZoom<OIVolume>(
    zoomConfig,
    setZoomConfig,
    'strike',
    oiVolumeData,
  );

  useEffect(() => {
    async function generateChartData() {
      const modelData: RawModelRow[] = await getModels(
        ['OI', 'OI_change', 'volume'],
        selectedSym,
      );
      const modelToRows: any = modelData.reduce(
        (o, r) => ({
          ...o,
          [r.model]: {
            strike_list: JSON.parse(r.strike_list),
            current_list: JSON.parse(r.current_list),
            next_exp_list: JSON.parse(r.next_exp_list),
          },
        }),
        {},
      );
      const { OI: oi, OI_change: oiChange, volume } = modelToRows;
      const strike2data = new Map();
      oi.strike_list.forEach((strike: number, idx: number) => {
        const roundedStrike = roundStrike(strike);
        const prev = strike2data.get(roundedStrike) ?? {};
        strike2data.set(roundedStrike, {
          ...prev,
          call_oi: oi.next_exp_list[idx] + (prev?.call_oi ?? 0),
          put_oi: oi.current_list[idx] + (prev?.put_oi ?? 0),
        });
      });
      oiChange.strike_list.forEach((strike: number, idx: number) => {
        const roundedStrike = roundStrike(strike);
        const prev = strike2data.get(roundedStrike) ?? {};
        strike2data.set(roundedStrike, {
          ...prev,
          put_oi_change: oiChange.current_list[idx] + (prev.put_oi_change ?? 0),
          call_oi_change:
            oiChange.next_exp_list[idx] + (prev.call_oi_change ?? 0),
        });
      });
      volume.strike_list.forEach((strike: number, idx: number) => {
        const roundedStrike = roundStrike(strike);
        const prev = strike2data.get(roundedStrike) ?? {};
        strike2data.set(roundedStrike, {
          ...prev,
          put_volume: volume.current_list[idx] + (prev.put_volume ?? 0),
          call_volume: volume.next_exp_list[idx] + (prev.call_volume ?? 0),
        });
      });
      for (const [roundedStrike, val] of strike2data.entries()) {
        val.strike = roundedStrike;
      }
      const sortedData = [...strike2data.values()].sort(
        (a: OIVolume, b: OIVolume) => a.strike - b.strike,
      );
      setOiVolumeData(sortedData);
      updateBrushZoomConfig(zoomConfig, sortedData, setZoomConfig);
    }
    generateChartData();
  }, [getModels, setZoomConfig, setOiVolumeData, selectedSym]);

  const ticks = useMemo(() => {
    const strikes = zoomConfig?.data?.map((d) => d.strike);
    return strikes
      ? getTicksBrushed(strikes, zoomConfig, TIMESTAMP_TICK_CONFIG)
      : [];
  }, [zoomConfig]);

  // TODO: Introduce 2nd y-axis when showing volume?
  const yLabel =
    oiType === OI_TYPE.TOTAL
      ? 'Open Interest & Volume'
      : 'Change in Open Interest';

  const controls = (
    <OIVolumeControls
      oiType={oiType}
      setOptType={setOptType}
      optType={optType}
      setOiType={setOiType}
      oiVolumeData={oiVolumeData}
    />
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        gap: '8px',
        ...containerStyleOverrides,
      }}
    >
      <IndicesHeader
        type={IndicesContentType.OI_VOLUME}
        title="Open Interest & Volume"
        expandable
        customController={controls}
        symbol={selectedSym}
        symSelectorSettings={symSelectorSettings}
      />
      {zoomConfig.data && (
        <ChartWatermarkContainer
          ref={ref}
          style={{ ...chartStyleOverrides }}
          size={25}
          offsetX={55}
          offsetY={40}
        >
          <ResponsiveContainer>
            <BarChart
              margin={{ ...DEFAULT_CHART_MARGINS, right: 45 }}
              stackOffset="silhouette"
              {...zoomChartConfig}
            >
              <CartesianGrid
                strokeDasharray="1 10"
                stroke={theme.palette.gray}
              />
              <XAxis
                allowDataOverflow
                dataKey="strike"
                type="number"
                tick={{ fontSize: 11 }}
                ticks={ticks}
                tickFormatter={(v: number) => formatAsCurrency(v, true)}
                label={{
                  ...DEFAULT_X_AXIS_STYLES,
                  value: 'Strike',
                  offset: 3,
                }}
                domain={['dataMin', 'dataMax']}
                tickCount={10}
              />
              <Brush
                dataKey="strike"
                tickFormatter={(v: number) => formatAsCurrency(v, true)}
                startIndex={zoomConfig.leftIdx}
                endIndex={zoomConfig.rightIdx}
                onChange={(brushIndices: any) =>
                  setZoomConfig((prev) => ({
                    ...prev,
                    leftIdx: brushIndices.startIndex,
                    rightIdx: brushIndices.endIndex,
                  }))
                }
                height={20}
                travellerWidth={15}
                stroke={theme.palette.gray}
                fill={theme.palette.background.paper}
                alwaysShowText
              />
              <YAxis
                allowDataOverflow
                domain={['dataMin', 'dataMax']}
                tickFormatter={formatAsCompactNumberCallback}
                tick={{ fontSize: 11 }}
                label={{
                  ...DEFAULT_Y_AXIS_STYLES,
                  value: yLabel,
                }}
              />
              <Tooltip
                formatter={(v: string) => parseFloat(v).toLocaleString()}
                labelFormatter={(value: number) =>
                  `Strike: $${Math.round(value).toLocaleString()}`
                }
                itemStyle={{ fontSize: '11px' }}
                contentStyle={{
                  color: theme.palette.text.primary,
                  border: 'none',
                  backgroundColor: theme.palette.background.paper,
                  boxShadow: theme.palette.shadows.paperBoxShadow,
                }}
                separator=": "
              />
              {oiType === OI_TYPE.TOTAL && (
                <Bar
                  dataKey={`${optType}_volume`}
                  name="Volume Traded"
                  fill={theme.palette.indices.delta.all}
                  barSize={2}
                />
              )}
              <Bar
                dataKey={`${optType}_oi${keySuffix}`}
                name={
                  oiType === OI_TYPE.CHANGE
                    ? 'Open Interest Change'
                    : 'Total Open Interest'
                }
                barSize={2}
                fill={
                  optType === PutCall.PUT
                    ? theme.palette.core.put
                    : theme.palette.core.call
                }
              />
              <ReferenceLine y={0} strokeOpacity={0.5} />
              {getZoomConfigRefArea(zoomConfig)}
            </BarChart>
          </ResponsiveContainer>
        </ChartWatermarkContainer>
      )}
    </Box>
  );
};
